/* import __COLOCATED_TEMPLATE__ from './move-content-select.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */

import type AppService from 'embercom/services/app-service';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Block } from '@intercom/interblocks.ts';
import { restartableTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { get } from 'embercom/lib/ajax';
import type ContentReviewRequest from 'embercom/models/content-service/content-review-request';
import { EntityType } from 'embercom/models/data/entity-types';
import { objectNames } from 'embercom/models/data/matching-system/matching-constants';
import type KnowledgeHubDrawerEditorService from 'embercom/services/knowledge-hub-drawer-editor-service';
import type SuggestionService from 'embercom/services/suggestion-service';
import type { EntityTupleWithTitle } from '../../content-select/select';
import { type ReviewableContent } from 'embercom/services/suggestion-service';
import type { ContentSearchResponse } from 'embercom/lib/content-service/search-api';
import AiContentLibraryApi from 'embercom/lib/ai-content-library/list-api';
import { ContentWrapperType } from 'embercom/models/content-service/content-wrapper';
import { CAN_MANAGE_KNOWLEDGE_BASE_CONTENT } from 'embercom/lib/knowledge-hub/constants';

type PartialSearchResponseContentWrapper = {
  entity_id: number;
  contents: { content_id: number; content_type: EntityType }[];
  title: string;
};

interface RelatedContentResponse {
  entity_id: string;
  entity_type: string;
  passage_blocks: Block[];
  title: string;
}

interface Args {
  reviewRequest: ContentReviewRequest;
  activeContent: ReviewableContent;
}

interface Signature {
  Element: HTMLDivElement;
  Args: Args;
  Blocks: {
    default: [];
  };
}

export default class MoveContentSelect extends Component<Signature> {
  @service declare notificationsService: $TSFixMe;
  @service declare suggestionService: SuggestionService;
  @service declare appService: AppService;
  @service declare knowledgeHubDrawerEditorService: KnowledgeHubDrawerEditorService;
  @service declare intercomEventService: $TSFixMe;
  @service declare permissionsService: $TSFixMe;

  @tracked contentEntities: EntityTupleWithTitle[] = [];
  @tracked searchTerm = '';
  @tracked contentApi: AiContentLibraryApi;
  @tracked isRelatedContentSearch = true;
  @tracked showModal = false;
  @tracked selectedEntityId: number | null = null;
  @tracked selectedEntityType: EntityType | null = null;

  constructor(owner: unknown, args: Args) {
    super(owner, args);

    this.contentApi = new AiContentLibraryApi(
      owner,
      {
        content_wrapper_types: [ContentWrapperType.SUPPORT_CONTENT],
        per_page: 10,
        app_id: this.appService.app.id,
        object_types: this.searcheableEntityTypes,
      },
      { persistToStore: false },
    );
  }

  get searcheableEntityTypes() {
    return [EntityType.ContentSnippet, EntityType.ArticleContent];
  }

  get selectedEntityTitle() {
    return this.contentEntities.find((entity) => entity.entityId === this.selectedEntityId)?.title;
  }

  @restartableTask
  *fetchRelatedContent(): Generator<
    Promise<RelatedContentResponse[]>,
    void,
    RelatedContentResponse[]
  > {
    if (!this.args.reviewRequest) {
      return;
    }
    try {
      this.isRelatedContentSearch = true;
      let response = yield get(
        `/ember/content_service/content_review_requests/${this.args.reviewRequest.get('id')}/related_content`,
        {
          top_k: 10,
          app_id: this.appService.app.id,
        },
      );
      this.contentEntities = response.map((entity) => ({
        entityId: Number(entity.entity_id),
        entityType: entity.entity_type as unknown as EntityType,
        title: entity.title,
      }));
    } catch (error) {
      // Fall back to search if related content fails
      this.searchTerm = '';
      taskFor(this.searchTask).perform();
    }
  }

  @restartableTask
  *searchTask() {
    let searchResponse: ContentSearchResponse = yield this.contentApi.search(this.searchTerm);
    this.contentEntities =
      searchResponse?.content_wrappers
        .map((wrapper: PartialSearchResponseContentWrapper) => ({
          entityId: wrapper.contents[0].content_id,
          entityType: wrapper.contents[0].content_type,
          title: wrapper.title,
        }))
        .filter((item) => {
          if (!this.args.activeContent) {
            return true;
          }
          return !(
            item.entityId === Number(this.args.activeContent.id) &&
            item.entityType === this.args.activeContent.entityType
          );
        }) ?? [];
  }

  get isLoadingContent() {
    return taskFor(this.fetchRelatedContent).isRunning || taskFor(this.searchTask).isRunning;
  }

  @action
  handleSearchTermChange(value: string) {
    this.searchTerm = value;
    this.contentEntities = [];
    if (!this.searchTerm.trim()) {
      // use related content search if the user cleared their search term
      this.isRelatedContentSearch = true;
      taskFor(this.fetchRelatedContent).perform();
    } else {
      this.isRelatedContentSearch = false;
      taskFor(this.searchTask).perform();
    }
  }

  @action
  async handleContentSelect(entityId: number, entityType: EntityType) {
    try {
      await this.permissionsService.checkPermission(CAN_MANAGE_KNOWLEDGE_BASE_CONTENT);
    } catch (e) {
      return;
    }
    if (!this.args.reviewRequest) {
      return;
    }

    this.trackAnalyticsEvent(entityId, entityType);

    this.selectedEntityId = entityId;
    this.selectedEntityType = entityType;
    this.showModal = true;
  }

  @action
  async handleContentClick(entityId: number, entityType: EntityType) {
    try {
      await this.permissionsService.checkPermission(CAN_MANAGE_KNOWLEDGE_BASE_CONTENT);
    } catch (e) {
      return;
    }
    if (!this.args.reviewRequest) {
      return;
    }
    //Intentionally not waiting on this task, we want to move content in the background and notify the UI in the meantime
    taskFor(this.suggestionService.replaceReviewRequest).perform(
      this.args.reviewRequest.get('id'),
      entityId,
      entityType,
    );

    this.knowledgeHubDrawerEditorService.openReviewDrawer({
      activeContentId: entityId,
      activeContentType: objectNames[entityType],
    });
  }

  @action
  handleMoveContent() {
    if (this.selectedEntityId && this.selectedEntityType) {
      this.handleContentClick(this.selectedEntityId, this.selectedEntityType);
      this.showModal = false;
    }
  }

  @action
  onSelectClose() {
    this.isRelatedContentSearch = true;
    this.contentEntities = [];
    this.searchTerm = '';
    taskFor(this.searchTask).cancelAll();
    taskFor(this.fetchRelatedContent).cancelAll();
  }

  @action
  onSelectOpen() {
    taskFor(this.fetchRelatedContent).perform();
  }

  private trackAnalyticsEvent(entityId: number, entityType: EntityType) {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'move_content_suggestion_select',
      entity_id: entityId,
      entity_type: entityType,
      recommended_content: this.isRelatedContentSearch,
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::ContentEditor::ContentReview::MoveContentSelect': typeof MoveContentSelect;
    'knowledge-hub/content-editor/content-review/move-content-select': typeof MoveContentSelect;
  }
}
